import React, { useEffect, useState } from 'react';
import { storiesApi } from '../../api/stories';
import { NOTE_CHANGED_EVENT } from '../../utils/noteChanged';
import { BOOK_TOOL_STORY_PROGRESS, CLOSE_STORY_PROGRESS } from '../../utils/writerCopy';
import StoryProgressMark from './StoryProgressMark';
import './StoryProgressPanel.css';

export const STORY_PROGRESS_LOADING = 'Opening story progress.';
export const STORY_PROGRESS_ERROR = 'Story progress could not be opened.';
export const STORY_PROGRESS_RETRY = 'Try again';
export const STORY_PROGRESS_NO_BOOK = 'Open a book to see story progress.';
// A read that never settles must not leave the opening line up.
export const STORY_PROGRESS_LOAD_LIMIT_MS = 20000;

function lanesPayload(data) {
  return Boolean(data && Array.isArray(data.lanes));
}

export default function StoryProgressPanel({ storyId, textScale = 1, onClose = null }) {
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState(storyId ? 'loading' : 'no-book');
  const [attempt, setAttempt] = useState(0);
  const scale = Number(textScale);
  const zoom = Number.isFinite(scale) && scale > 0 ? scale : 1;

  useEffect(() => {
    if (!storyId) {
      setPayload(null);
      setStatus('no-book');
      return undefined;
    }
    let alive = true;
    let generation = 0;
    const timer = window.setTimeout(() => {
      if (!alive) return;
      setStatus((current) => (current === 'loading' ? 'error' : current));
    }, STORY_PROGRESS_LOAD_LIMIT_MS);
    const stopTimer = () => window.clearTimeout(timer);

    const load = ({ initial }) => {
      const ticket = generation + 1;
      generation = ticket;
      if (initial) {
        setStatus('loading');
        setPayload(null);
      }
      // Stored lanes and the readiness line. Not a model call.
      // A newer read must not throw away an earlier one that already finished
      // while the opening line is still up.
      storiesApi.getStoryProgress(storyId, { cache: 'no-store' })
        .then((data) => {
          if (!alive) return;
          const latest = ticket === generation;
          if (!lanesPayload(data)) {
            if (!latest) return;
            setStatus((current) => (current === 'ready' ? current : 'error'));
            stopTimer();
            return;
          }
          if (!latest) {
            setPayload((current) => current || data);
            setStatus('ready');
            stopTimer();
            return;
          }
          setPayload(data);
          setStatus('ready');
          stopTimer();
        })
        .catch(() => {
          if (!alive || ticket !== generation) return;
          setStatus((current) => (current === 'ready' ? current : 'error'));
          stopTimer();
        });
    };

    load({ initial: true });

    const onNoteChanged = (event) => {
      const changedId = event?.detail?.storyId;
      if (changedId && String(changedId) !== String(storyId)) return;
      load({ initial: false });
    };
    window.addEventListener(NOTE_CHANGED_EVENT, onNoteChanged);
    return () => {
      alive = false;
      stopTimer();
      window.removeEventListener(NOTE_CHANGED_EVENT, onNoteChanged);
    };
  }, [storyId, attempt]);

  let body = null;
  if (status === 'no-book') {
    body = <p className="story-progress__status">{STORY_PROGRESS_NO_BOOK}</p>;
  } else if (status === 'loading') {
    body = <p className="story-progress__status" role="status">{STORY_PROGRESS_LOADING}</p>;
  } else if (status === 'error' || !payload) {
    body = (
      <div role="alert">
        <p className="story-progress__status">{STORY_PROGRESS_ERROR}</p>
        <button
          type="button"
          className="story-progress__retry"
          onClick={() => setAttempt((value) => value + 1)}
        >
          {STORY_PROGRESS_RETRY}
        </button>
      </div>
    );
  } else {
    const readiness = payload.readiness;
    body = (
      <>
        <ul className="story-progress__lanes">
          {payload.lanes.map((lane) => (
            <li
              key={lane.id}
              className="story-progress__row"
              title={lane.tip || undefined}
              data-testid={`story-progress-${lane.id}`}
              data-state={lane.state}
            >
              <StoryProgressMark state={lane.state} />
              <span className="story-progress__text">
                <span className="story-progress__label">{lane.label}</span>
                {lane.headline ? (
                  <span className="story-progress__headline">{lane.headline}</span>
                ) : null}
              </span>
              <span className="story-progress__sr">{lane.tip}</span>
            </li>
          ))}
        </ul>
        {readiness?.label ? (
          <p
            className="story-progress__readiness"
            data-testid="story-progress-readiness"
            title={readiness.tip || undefined}
          >
            {readiness.label}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <section
      className="story-progress"
      data-testid="story-progress"
      aria-labelledby="story-progress-title"
    >
      <header className="story-progress__header">
        <h2 id="story-progress-title">{BOOK_TOOL_STORY_PROGRESS}</h2>
        {onClose && (
          <button
            type="button"
            className="story-progress__close"
            onClick={onClose}
            aria-label={CLOSE_STORY_PROGRESS}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </header>
      <div
        className="story-progress__scale"
        data-testid="story-progress-scale"
        style={{ zoom }}
      >
        {body}
      </div>
    </section>
  );
}
